// src/hooks/useOverviewSeries.js
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchVitalSignals } from "../redux/thunks/vitalThunks";
import { selectVitalSignals } from "../redux/selectors/vitalSignsSelectors";

/** ---- Utils dates ---- */
const safeDate = (d) => {
  const t = new Date(d ?? Date.now());
  return Number.isNaN(t.getTime()) ? new Date() : t;
};
const diffInMonths = (from, to) => {
  const a = safeDate(from);
  const b = safeDate(to);
  return (a.getFullYear() - b.getFullYear()) * 12 + (a.getMonth() - b.getMonth());
};

/** Filtre temporel: ALL | 1M | 6M | 1Y */
const passesTimeFilter = (date, key) => {
  if (!key || key === "ALL") return true;
  const now = new Date();
  const months = key === "1M" ? 1 : key === "6M" ? 6 : key === "1Y" ? 12 : 0;
  if (!months) return true;
  return diffInMonths(now, date) < months;
};

/** ---- Parser robuste BP "120/80", "120 - 80", "120_80", etc. ---- */
// Parse blood pressure string into numbers
const parseBP = (bpStr) => {
  if (!bpStr || typeof bpStr !== "string") return { sys: undefined, dia: undefined };
  const cleaned = bpStr.replace(/[^\d/]/g, "/");
  const parts = cleaned.split("/").filter(Boolean);
  if (!parts.length) return { sys: undefined, dia: undefined };
  const s = Number(parts[0]);
  const d = Number(parts[1]);
  return {
    sys: Number.isFinite(s) ? s : undefined,
    dia: Number.isFinite(d) ? d : undefined,
  };
};

/** Libellé X (ex: "2025-11-10" -> "10 Nov 25") */
const fmtX = (d) => {
  const dt = safeDate(d);
  return dt.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
};

export default function useOverviewSeries(userId) {
  const dispatch = useDispatch();
  const signals = useSelector(selectVitalSignals);

  // fetch vitals 1x per userId
  const fetchedRef = useRef(null);
  useEffect(() => {
    if (!userId) return;
    if (fetchedRef.current !== userId) {
      fetchedRef.current = userId;
      dispatch(fetchVitalSignals({ userId }));
    }
  }, [userId, dispatch]);

  const typeMap = useMemo(
    () => ({
      bloodSugar: ["GLYCEMIA", "BLOOD_GLUCOSE"],
      heartRate: ["HEART_RATE"],
      respiratory: ["RESP_RATE", "RESPIRATORY_RATE"],
      bloodPressure: ["BP_SYS", "BP_DIA", "BLOOD_PRESSURE"],
    }),
    []
  );

  /** Série mono (gly, hr, rr) */
  const getSingleSeries = (tabKey, timeKey) => {
    const types = typeMap[tabKey] || [];
    const pts = [];
    const labels = [];

    const filtered = (signals || []).filter((s) => types.includes(s?.type));
    filtered.sort((a, b) => new Date(a?.measuredAt || a?.createdAt || 0) - new Date(b?.measuredAt || b?.createdAt || 0));

    for (const r of filtered) {
      const dt = safeDate(r?.measuredAt || r?.createdAt || r?.addedAt || r?.date);
      if (!passesTimeFilter(dt, timeKey)) continue;
      const n = Number(r?.value);
      if (!Number.isFinite(n)) continue;
      pts.push(n);
      labels.push(fmtX(dt));
    }
    const min = pts.length ? Math.min(...pts) : 0;
    const max = pts.length ? Math.max(...pts) : 1;
    return { points: pts, labels, min, max, count: pts.length };
  };

  /** Série bi-valeur pour Blood Pressure (SYS/DIA) */
  const getBPSeries = (timeKey) => {
    const sys = [];
    const dia = [];
    const labels = [];

    // Regrouper par timestamp pour associer SYS & DIA
    const byDate = new Map();
    (signals || [])
      .filter((s) => typeMap.bloodPressure.includes(s?.type))
      .forEach((s) => {
        const dt = safeDate(s?.measuredAt || s?.createdAt || s?.addedAt || s?.date);
        if (!passesTimeFilter(dt, timeKey)) return;
        const key = dt.toISOString();
        const entry = byDate.get(key) || { date: dt };
        if (s.type === "BP_SYS") entry.sys = Number(s.value);
        else if (s.type === "BP_DIA") entry.dia = Number(s.value);
        else {
          // fallback if single string BP value is stored like "120/80"
          const parsed = parseBP(String(s.value || ""));
          if (parsed.sys) entry.sys = parsed.sys;
          if (parsed.dia) entry.dia = parsed.dia;
        }
        byDate.set(key, entry);
      });

    const rows = [...byDate.values()].sort((a, b) => a.date - b.date);
    for (const r of rows) {
      sys.push(Number.isFinite(r.sys) ? r.sys : null);
      dia.push(Number.isFinite(r.dia) ? r.dia : null);
      labels.push(fmtX(r.date));
    }

    const sysNums = sys.filter((x) => typeof x === "number");
    const diaNums = dia.filter((x) => typeof x === "number");
    const all = [...sysNums, ...diaNums];
    const min = all.length ? Math.min(...all) : 0;
    const max = all.length ? Math.max(...all) : 1;
    return { sys, dia, labels, min, max, count: labels.length };
  };

  /** API publique du hook */
  const getSeries = (tabKey, timeKey) => {
    if (tabKey === "bloodPressure") return getBPSeries(timeKey);
    return getSingleSeries(tabKey, timeKey);
  };

  /** Normal ranges (optionnel) */
  const normalZone = {
    bloodSugar: { min: 70, max: 140 },     // mg/dL (à ajuster si besoin)
    heartRate: { min: 60, max: 100 },      // bpm
    respiratory: { min: 12, max: 20 },     // rpm
  };

  return {
    history: signals,
    historyHasFetched: signals?.length > 0,
    getSeries,     // (tabKey, timeKey) => {points|sys/dia, labels, min, max, count}
    normalZone,
  };
}
